import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileKycDocument, useKycPack, useRemoveKycDocument } from '../../api/hooks';
import { useSession } from '../../auth/session';
import { Alert, Button, Chip, Panel, Spinner, Table, type Tone } from '../../ui';
import { megabytes, reasonDisplay, standing, type KycStanding } from './kyc-view';

/**
 * R-18's KYC panel — CR-57 / D105.
 *
 * D105 supersedes D94: KYC no longer gates the ACCOUNT, it gates the ORDER. So a new retailer lands
 * here with an account, can browse and fill a cart, and files their own documents; Admin approves
 * the pack on A-04; only then can a PO be submitted. This panel is where they do the filing.
 *
 * **The rejection reason is the most important thing on it.** A retailer who is not told which file
 * was wrong uploads the same one again, and then rings their account manager — the very thing this
 * feature exists to prevent. So it leads the panel, in Miraki's own words and unabridged, and it
 * stays visible after they have re-filed (the account is back in review, the reason is still the
 * last thing Miraki said) until Admin's next decision replaces it.
 *
 * The state comes from `me` (so every login on the account can see it) and the documents from
 * `GET /shop/kyc-documents`, which only the Owner (`shop.profile`) may call — filing an account's
 * identity documents is the job of whoever administers the account, and nobody else is sent a
 * control that would be refused.
 *
 * The window is the server's: `mayFile` says whether filing will work, and the server re-checks it
 * from the database on every request.
 */
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,.doc,.docx,application/msword';

const TONE: Record<KycStanding, Tone> = { NEEDED: 'stop', REVIEW: 'run', APPROVED: 'done', REJECTED: 'stop' };

export function KycPanel() {
  const { t } = useTranslation();
  const { me } = useSession();
  const isOwner = me?.kind === 'retailer' && me.permissions.includes('shop.profile');
  const pack = useKycPack(isOwner);
  const file = useFileKycDocument();
  const remove = useRemoveKycDocument();
  const picker = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  if (me?.kind !== 'retailer') return null;

  // The state every login can see, from `me`; the documents only the Owner is sent.
  const status = pack.data?.kycStatus ?? me.kycStatus;
  const reason = pack.data?.kycRejectionReason ?? me.kycRejectionReason;
  const documents = pack.data?.documents ?? [];
  const stand = standing(status, documents.length);
  const shown = reasonDisplay(status, reason);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    e.target.value = ''; // so the same file can be chosen again after a refusal
    if (!chosen) return;
    setProblem(null);
    try {
      await file.mutateAsync(chosen);
    } catch (err) {
      // The API says what is wrong with the file — the count, the size, or that it is not the
      // format its name claims — and says it plainly, so it is shown as it arrived.
      setProblem((err as Error).message);
    }
  };

  const onRemove = async (id: string) => {
    setProblem(null);
    setRemoving(id);
    try {
      await remove.mutateAsync(id);
    } catch {
      setProblem(t('shop.kyc.removeFailed'));
    } finally {
      setRemoving(null);
    }
  };

  const mayFile = isOwner && (pack.data?.mayFile ?? status !== 'APPROVED');
  const full = (pack.data?.remaining ?? 1) <= 0;

  return (
    <Panel
      title={t('shop.kyc.title')}
      pad
      actions={<Chip tone={TONE[stand]}>{t(`shop.kyc.status.${stand}`)}</Chip>}
    >
      {/* The reason leads — see the note above. Shown while REJECTED, and while re-filed and awaiting review. */}
      {shown !== 'none' ? (
        <Alert tone={shown === 'rejected' ? 'stop' : 'them'}>
          <b>{shown === 'rejected' ? t('shop.kyc.rejectedTitle') : t('shop.kyc.earlierNote')}</b>{' '}
          {shown === 'rejected' ? t('shop.kyc.rejectedLead') : null}
          <div style={{ marginTop: 'var(--mk-space-2)', whiteSpace: 'pre-wrap' }}>{reason ?? t('shop.kyc.noReason')}</div>
        </Alert>
      ) : null}

      {stand === 'APPROVED' ? <p>{t('shop.kyc.approvedBody')}</p> : null}
      {stand === 'REVIEW' ? <p>{t('shop.kyc.reviewBody')}</p> : null}
      {stand === 'NEEDED' || stand === 'REJECTED' ? <p>{t('shop.kyc.intro')}</p> : null}

      {!isOwner ? (
        <p className="mk-dim">{t('shop.kyc.ownerOnly')}</p>
      ) : pack.isLoading ? (
        <Spinner />
      ) : pack.error ? (
        <Alert tone="stop">{t('shop.kyc.loadFailed')}</Alert>
      ) : (
        <>
          {documents.length === 0 ? (
            <p className="mk-dim">{t('shop.kyc.none')}</p>
          ) : (
            <Table
              rows={documents}
              rowKey={(d) => d.id}
              columns={[
                { header: t('shop.kyc.colFile'), render: (d) => d.fileName },
                { header: t('shop.kyc.colSize'), numeric: true, render: (d) => megabytes(d.sizeBytes) },
                { header: t('shop.kyc.colSent'), render: (d) => d.createdAt.slice(0, 10) },
                {
                  header: '',
                  numeric: true,
                  render: (d) => (mayFile ? (
                    <Button size="sm" tone="ghost" disabled={removing !== null} onClick={() => { void onRemove(d.id); }}>
                      {removing === d.id ? t('shop.kyc.removing') : t('shop.kyc.remove')}
                    </Button>
                  ) : null),
                },
              ]}
            />
          )}

          {problem ? <Alert tone="stop">{problem}</Alert> : null}

          {mayFile ? (
            <div style={{ marginTop: 'var(--mk-space-3)' }}>
              <input ref={picker} type="file" accept={ACCEPT} hidden onChange={(e) => { void onPick(e); }} />
              <Button disabled={file.isPending || full} onClick={() => picker.current?.click()}>
                {file.isPending ? t('shop.kyc.sending') : t('shop.kyc.choose')}
              </Button>
              <p className="mk-hint">
                {full
                  ? t('shop.kyc.limitReached')
                  : t('shop.kyc.limits', {
                    max: pack.data?.documentsAccepted ?? 5, mb: 8, count: documents.length,
                  })}
              </p>
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}
